(() => {
  'use strict';
  if (window.FrilloraCartExpress) return;
  const hosts = [...document.querySelectorAll('[data-fr-cart-express-host]')];
  if (!hosts.length) return;
  const bootstraps = [...document.querySelectorAll('[data-fr-cart-express-bootstrap]')];
  const drawer = document.querySelector('[data-cart-drawer]');
  const root = window.Shopify?.routes?.root || '/';
  let generation = 0, current = null, activeRequest = null, readyKey = null, readyGroup = null;
  const signature = item => JSON.stringify([Number(item.variant_id || item.id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null]);
  const fingerprint = items => JSON.stringify(items.map(item => [signature(item), item.quantity]).sort());
  const stateKey = state => JSON.stringify([state.country, state.currency, state.itemCount, state.total, state.fingerprint]);
  const validState = state => state && !state.blocked && Number.isInteger(state.itemCount) && state.itemCount > 0 && Number.isInteger(state.total) && state.total >= 0 && /^[A-Z]{2}$/.test(state.country) && /^[A-Z]{3}$/.test(state.currency) && typeof state.fingerprint === 'string';
  const purchaseBlocked = () => document.visibilityState !== 'visible' || typeof window.FrilloraCartEnhance?.isPurchaseBlocked !== 'function' || window.FrilloraCartEnhance.isPurchaseBlocked();
  const setHostState = state => hosts.forEach(host => {
    host.hidden = true;
    host.inert = true;
    host.dataset.frCartExpressState = state;
    const label = host.closest?.('.fr-cart-payment-slot')?.querySelector('[data-fr-cart-wallet-status]');
    if (label) {
      const key = state === 'unavailable' ? 'unavailableLabel' : state === 'empty' ? 'selectLabel' : 'loadingLabel';
      if (label.dataset[key]) label.textContent = label.dataset[key];
    }
  });
  const clear = (state, remove = true) => {
    generation += 1;
    activeRequest?.controller.abort();
    activeRequest = null;
    readyKey = null;
    readyGroup = null;
    setHostState(state);
    if (remove) hosts.forEach(host => host.replaceChildren());
  };
  const targetHost = () => {
    if (drawer?.classList.contains('is-open')) return hosts.find(host => host.dataset.frCartExpressHost === 'drawer') || null;
    return hosts.find(host => host.dataset.frCartExpressHost === 'page') || hosts.find(host => host.dataset.frCartExpressHost === 'drawer') || null;
  };
  const place = () => {
    if (!readyGroup || !validState(current) || purchaseBlocked() || readyKey !== stateKey(current)) return;
    const host = targetHost();
    if (!host) return;
    setHostState('ready');
    if (readyGroup.parentElement !== host) host.replaceChildren(readyGroup);
    host.hidden = false;
    host.inert = false;
  };
  const matches = (data, expected) => data && data.country === expected.country && data.currency === expected.currency && data.itemCount === expected.itemCount && data.total === expected.total && Array.isArray(data.items) && data.items.every(item => Number.isInteger(item.quantity) && item.quantity > 0 && Number.isSafeInteger(Number(item.variant_id)) && Number(item.variant_id) > 0) && data.items.reduce((sum, item) => sum + item.quantity, 0) === data.itemCount && fingerprint(data.items) === expected.fingerprint;
  const shopPayOnly = (native, expected) => {
    const components = [...native.querySelectorAll('shopify-accelerated-checkout-cart')];
    if (components.length !== 1) throw Error('Native checkout shape changed');
    const component = components[0];
    if (component.ownerDocument === document && component.isConnected) throw Error('Native checkout already connected');
    const configs = JSON.parse(component.getAttribute('wallet-configs') || 'null');
    if (!Array.isArray(configs)) throw Error('Native checkout configuration unavailable');
    const selected = configs.filter(config => config?.name === 'shop_pay');
    const shopId = Number(component.getAttribute('shop-id'));
    if (selected.length !== 1 || !Number.isSafeInteger(shopId) || shopId <= 0 || Number(selected[0].wallet_params?.shopId) !== shopId || component.getAttribute('buyer-country') !== expected.country || component.getAttribute('buyer-currency') !== expected.currency) throw Error('Native Shop Pay unavailable');
    component.setAttribute('wallet-configs', JSON.stringify(selected));
  };
  const waitForDefinition = signal => {
    if (!window.customElements?.whenDefined) return Promise.reject(Error('Native checkout unavailable'));
    return new Promise((resolve, reject) => {
      const abort = () => reject(Error('Native checkout cancelled'));
      if (signal.aborted) { abort(); return; }
      signal.addEventListener('abort', abort, {once: true});
      window.customElements.whenDefined('shopify-accelerated-checkout-cart').then(() => {
        signal.removeEventListener('abort', abort);
        if (signal.aborted) abort(); else resolve();
      }, reject);
    });
  };
  async function load(expected, key) {
    const token = ++generation, controller = new AbortController();
    activeRequest = {controller, key};
    setHostState('loading');
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(root + 'cart', location.origin);
      url.searchParams.set('section_id', 'fr-cart-express');
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (/^\d+$/.test(preview || '')) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url.href, {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'text/html'}, signal: controller.signal});
      if (!response.ok) throw Error('Native checkout unavailable');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const metadata = parsed.querySelector('[data-fr-cart-express-data]');
      const native = parsed.querySelector('[data-fr-cart-express-native]');
      if (!metadata || !native?.querySelector('shopify-accelerated-checkout-cart') || !matches(JSON.parse(metadata.textContent), expected)) throw Error('Native checkout context changed');
      shopPayOnly(native, expected);
      if (token !== generation || !validState(current) || purchaseBlocked() || stateKey(current) !== key) return;
      const host = targetHost();
      if (!host) return;
      const group = document.importNode(native, true);
      host.replaceChildren(group);
      await waitForDefinition(controller.signal);
      if (token !== generation || controller.signal.aborted || !validState(current) || purchaseBlocked() || stateKey(current) !== key) return;
      bootstraps.forEach(bootstrap => bootstrap.remove());
      readyGroup = group;
      readyKey = key;
      place();
    } catch {
      if (token === generation) {
        readyGroup = null;
        readyKey = null;
        setHostState('unavailable');
        hosts.forEach(host => host.replaceChildren());
      }
    } finally {
      clearTimeout(timer);
      if (token === generation) activeRequest = null;
    }
  }
  window.FrilloraCartExpress = {
    sync(state) {
      current = state ? {...state} : null;
      if (!validState(current) || purchaseBlocked()) { clear(current?.itemCount === 0 ? 'empty' : 'blocked'); return; }
      const key = stateKey(current);
      if (readyKey === key && readyGroup) { place(); return; }
      if (activeRequest?.key === key) return;
      clear('loading');
      void load({...current}, key);
    },
  };
  setHostState('blocked');
  if (drawer && window.MutationObserver) new MutationObserver(place).observe(drawer, {attributes: true, attributeFilter: ['class']});
  document.addEventListener('visibilitychange', () => { if (document.visibilityState !== 'visible') clear('blocked', false); });
  window.addEventListener('pagehide', () => clear('blocked', false));
  document.addEventListener('click', event => {
    const host = hosts.find(node => node.contains(event.target));
    if (host && (host.hidden || host.inert || !validState(current) || purchaseBlocked() || readyKey !== stateKey(current))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      clear('blocked');
    }
  }, true);
})();
